import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Collapse, IconButton, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import api from '../../../services/api';
import type { PriceHistoryRow } from '../../../services/api';
import { EmptyState, Section, StatusPill, TONES } from '../../../components/ui/PageKit';
import { errorMessage, fmtMoney2 } from '../shared';

// Historial del precio de lista y de los pactados por cliente: qué precio rige, desde cuándo y cuánto cambió.
// Las filas las graba el backend en cada cambio; las de meses pasados se cargan por /pricing/history/import.

type Filtro = 'todos' | 'lista' | 'pactados';

type Serie = {
  key: string;
  productName: string;
  customerName: string | null;
  points: PriceHistoryRow[]; // de la más vieja a la más nueva
};

const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString('es-AR', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: '2-digit' });

const varPct = (now?: number, before?: number) => (now && before ? now / before - 1 : null);
const fmtVar = (v: number | null) => (v == null ? '—' : `${v > 0 ? '+' : ''}${(v * 100).toFixed(1).replace('.', ',')}%`);
const varTone = (v: number | null) => (v == null || Math.abs(v) < 0.0005 ? 'neutral' : v > 0 ? 'ok' : 'bad');

const PriceHistorySection: React.FC = () => {
  const [rows, setRows] = useState<PriceHistoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    api.getPriceHistory().then(setRows).catch(e => setError(errorMessage(e, 'No se pudo traer el historial de precios')));
  }, []);

  const series = useMemo<Serie[]>(() => {
    const map = new Map<string, Serie>();
    for (const r of [...(rows ?? [])].reverse()) {
      if (filtro === 'lista' && r.customerId != null) continue;
      if (filtro === 'pactados' && r.customerId == null) continue;
      const key = `${r.productId}|${r.customerId ?? 'lista'}`;
      if (!map.has(key)) map.set(key, { key, productName: r.productName, customerName: r.customerName, points: [] });
      map.get(key)!.points.push(r);
    }
    return Array.from(map.values()).sort((a, b) =>
      a.productName.localeCompare(b.productName, 'es') || (a.customerName == null ? -1 : b.customerName == null ? 1 : a.customerName.localeCompare(b.customerName, 'es')));
  }, [rows, filtro]);

  return (
    <Section
      title="Precio de lista y pactados"
      description="Qué precio rige hoy para cada producto (lista y cada cliente con precio pactado), desde cuándo y cuánto cambió. Se guarda solo cada vez que alguien cambia un precio."
      actions={(
        <ToggleButtonGroup size="small" exclusive value={filtro} onChange={(_, v) => v && setFiltro(v)}>
          <ToggleButton value="todos" sx={{ textTransform: 'none', px: 1.5 }}>Todos</ToggleButton>
          <ToggleButton value="lista" sx={{ textTransform: 'none', px: 1.5 }}>Lista</ToggleButton>
          <ToggleButton value="pactados" sx={{ textTransform: 'none', px: 1.5 }}>Pactados</ToggleButton>
        </ToggleButtonGroup>
      )}
    >
      {error && <Typography color="error" sx={{ mb: 1 }}>{error}</Typography>}
      {!rows ? <Box sx={{ height: 80 }} /> : series.length === 0 ? (
        <EmptyState title="Todavía no hay cambios de precio registrados" hint="Aparecen apenas se cambie un precio de lista o un pactado." />
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 36 }} />
                <TableCell>Producto</TableCell>
                <TableCell>Lista / cliente</TableCell>
                <TableCell align="right">Rige hoy</TableCell>
                <TableCell>Desde</TableCell>
                <TableCell align="right">Contra el anterior</TableCell>
                <TableCell align="right">Contra el primero</TableCell>
                <TableCell>Evolución</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {series.map(s => <SerieRow key={s.key} serie={s} open={open === s.key} onToggle={() => setOpen(open === s.key ? null : s.key)} />)}
            </TableBody>
          </Table>
        </Box>
      )}
    </Section>
  );
};

const SerieRow: React.FC<{ serie: Serie; open: boolean; onToggle: () => void }> = ({ serie, open, onToggle }) => {
  const last = serie.points[serie.points.length - 1];
  const removed = last.price === 0; // pactado quitado: vuelve a la lista
  const priced = serie.points.filter(p => p.price > 0);
  const lastPriced = priced[priced.length - 1];
  const prevDifferent = [...priced].reverse().find(p => p.price !== lastPriced?.price);
  const vsPrev = removed ? null : varPct(lastPriced?.price, prevDifferent?.price);
  const vsFirst = removed || priced.length < 2 ? null : varPct(lastPriced?.price, priced[0].price);
  return (
    <>
      <TableRow hover onClick={onToggle} sx={{ cursor: 'pointer', '& > td': { borderBottom: open ? 0 : undefined } }}>
        <TableCell><IconButton size="small" aria-label="Ver cambios">{open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</IconButton></TableCell>
        <TableCell sx={{ fontWeight: 650 }}>{serie.productName}</TableCell>
        <TableCell sx={{ color: serie.customerName ? 'text.primary' : 'text.secondary' }}>{serie.customerName ?? 'Lista'}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700 }}>
          {removed ? <Typography component="span" variant="body2" color="text.secondary">sin pactado</Typography> : fmtMoney2(last.price)}
        </TableCell>
        <TableCell>{fmtDay(last.effectiveFrom)}</TableCell>
        <TableCell align="right"><StatusPill tone={varTone(vsPrev)}>{fmtVar(vsPrev)}</StatusPill></TableCell>
        <TableCell align="right">
          <StatusPill tone={varTone(vsFirst)}>{fmtVar(vsFirst)}</StatusPill>
          {vsFirst != null && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>desde {fmtDay(priced[0].effectiveFrom)}</Typography>}
        </TableCell>
        <TableCell><Sparkline points={priced} /></TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ p: 2.5, bgcolor: '#FBFAF8' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Desde</TableCell>
                    <TableCell align="right">Precio</TableCell>
                    <TableCell align="right">Cambio</TableCell>
                    <TableCell>Origen</TableCell>
                    <TableCell>Nota</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...serie.points].reverse().map((p, i, arr) => {
                    const prev = arr.slice(i + 1).find(x => x.price > 0);
                    const v = p.price > 0 ? varPct(p.price, prev?.price) : null;
                    return (
                      <TableRow key={p.id}>
                        <TableCell>{fmtDay(p.effectiveFrom)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 650 }}>{p.price > 0 ? fmtMoney2(p.price) : 'sin pactado'}</TableCell>
                        <TableCell align="right">{fmtVar(v)}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{p.source}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{p.note ?? ''}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

/** Precio en escalones (cada cambio rige hasta el siguiente). */
const Sparkline: React.FC<{ points: PriceHistoryRow[] }> = ({ points }) => {
  if (points.length < 2) return <Typography variant="caption" color="text.secondary">un solo precio</Typography>;
  const W = 110, H = 28;
  const t = points.map(p => new Date(p.effectiveFrom).getTime());
  const v = points.map(p => p.price);
  const t0 = t[0], t1 = Math.max(t[t.length - 1], t0 + 1);
  const lo = Math.min(...v), hi = Math.max(...v), span = hi - lo || 1;
  const x = (ms: number) => ((ms - t0) / (t1 - t0)) * (W - 4) + 2;
  const y = (n: number) => H - 3 - ((n - lo) / span) * (H - 6);
  let d = `M${x(t[0]).toFixed(1)},${y(v[0]).toFixed(1)}`;
  for (let i = 1; i < points.length; i++) d += ` H${x(t[i]).toFixed(1)} V${y(v[i]).toFixed(1)}`;
  return (
    <svg width={W} height={H} aria-hidden style={{ display: 'block' }}>
      <path d={d} fill="none" stroke="#F0561D" strokeWidth={1.75} strokeLinejoin="round" />
      <circle cx={x(t[t.length - 1])} cy={y(v[v.length - 1])} r={2.5} fill={TONES.ok.dot} />
    </svg>
  );
};

export default PriceHistorySection;
